import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth0 } from 'utils/auth'
import { useUserData } from 'components/contexts/UserDataContext'
import { logAnalyticsEvent } from './analyticsUtils'
import { AnalyticsEvent } from './AnalyticsEvent'

const INVITATION_CAMPAIGN = 'workspace_invitation'

const INVITATION_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'plan_id', 'uid', 'ts'] as const

interface InvitationParams {
  utm_source: string | null
  utm_medium: string | null
  utm_campaign: string | null
  plan_id: string | null
  uid: string | null
  ts: string | null
}

export const useWorkspaceInvitationTracking = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { isAuthenticated } = useAuth0()
  const { baseUserData } = useUserData()

  const hasFired = useRef(false)

  const capturedParams = useRef<InvitationParams | null>(null)
  if (capturedParams.current === null) {
    const campaign = searchParams.get('utm_campaign')
    if (campaign === INVITATION_CAMPAIGN) {
      capturedParams.current = {
        utm_source: searchParams.get('utm_source'),
        utm_medium: searchParams.get('utm_medium'),
        utm_campaign: campaign,
        plan_id: searchParams.get('plan_id'),
        uid: searchParams.get('uid'),
        ts: searchParams.get('ts'),
      }
    }
  }

  useEffect(() => {
    if (!capturedParams.current) return
    if (!isAuthenticated || !baseUserData) return
    if (hasFired.current) return
    hasFired.current = true

    const params = capturedParams.current

    logAnalyticsEvent(AnalyticsEvent.WORKSPACE_INVITATION, {
      utm_source: params.utm_source,
      utm_medium: params.utm_medium,
      utm_campaign: params.utm_campaign,
      workspaceId: params.plan_id,
      invitedBy: params.uid,
      timestamp: params.ts,
      userEmail: baseUserData.email,
      userId: baseUserData.id,
    })

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      for (const key of INVITATION_PARAMS) {
        next.delete(key)
      }
      return next
    }, { replace: true })
  }, [isAuthenticated, baseUserData, setSearchParams])
}
